import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { QUERY } from 'api/Query';
import type { JSX } from 'react';
import { PlaceholderList } from 'ts/commons/Placeholders';
import { Message } from 'ts/components/Message';
import { Popup } from 'ts/components/Popup';
import { ToastNotification } from 'ts/components/Toast';
import { IssueLikeTable } from 'ts/perspectives/search/results/IssueTable';
import { EIssueReferenceType, type EIssueReferenceTypeEntry } from 'typedefs/EIssueReferenceType';
import type { IssueReference } from 'typedefs/IssueReference';
import type { UserResolvedSpecItem } from 'typedefs/UserResolvedSpecItem';
import type { UserResolvedTeamscaleIssue } from 'typedefs/UserResolvedTeamscaleIssue';

/** A teamscale issue with its type */
type UserResolvedTeamscaleIssueWithType = UserResolvedTeamscaleIssue & { type: EIssueReferenceTypeEntry };

/** The details for a set of {@link IssueReference}s (which can be spec items and/or issues) */
type IssueReferenceDetails = {
	issueReferenceDetails: Map<string, UserResolvedTeamscaleIssueWithType>;
	specReferenceDetails: Map<string, UserResolvedTeamscaleIssueWithType>;
	referenceDetails: Map<string, UserResolvedTeamscaleIssueWithType>;
};

type IssueLikeReferencePopupProps = {
	triggerText: string;
	issueReferences: IssueReference[];
	project: string;
};

/** A popup that, on hover, shows issues/spec items referenced in the source code. */
export function IssueLikeReferencePopup({
	triggerText,
	issueReferences,
	project
}: IssueLikeReferencePopupProps): JSX.Element {
	const issueReferenceDetails = useIssueLikeDetails(project, issueReferences);

	return (
		<Popup
			data-testid="issue-references-popup"
			hoverable
			position="bottom center"
			trigger={<span>{triggerText}</span>}
		>
			{issueReferenceDetails.isPending ? (
				<div className="w-96 py-2">
					<PlaceholderList numberOfLines={3} />
				</div>
			) : null}
			{issueReferenceDetails.isError ? (
				<Message error content={`Error loading issues: ${String(issueReferenceDetails.error)}`} />
			) : null}
			{issueReferenceDetails.isSuccess ? (
				<IssueLikeTable
					issueLikeElements={Array.from(issueReferenceDetails.data.referenceDetails.entries()).map(entry =>
						Object.assign(
							entry[1].issue,
							{ type: entry[1].type },
							{
								project
							}
						)
					)}
				/>
			) : null}
		</Popup>
	);
}

function useIssueLikeDetails(
	project: string,
	issueReferences: IssueReference[]
): UseQueryResult<IssueReferenceDetails> {
	return useQuery({
		queryKey: [
			project,
			issueReferences.map(reference => reference.type + '-' + reference.id.internalId + reference.id.connectorId)
		],
		queryFn: () => {
			return fetchDetailsForIssueReferences(project, issueReferences, true);
		},
		throwOnError: false
	});
}

/**
 * Given a list of issue references, fetches details for each of them. Filters out any references that could not be
 * resolved, so no null-entries are in the results.
 */
async function fetchDetailsForIssueReferences(
	project: string,
	issueReferences: IssueReference[],
	reThrowError = false
): Promise<IssueReferenceDetails> {
	const [issueReferenceDetails, specReferenceDetails] = await Promise.all([
		getIssueReferenceDetailsById(issueReferences, EIssueReferenceType.ISSUE, project, reThrowError),
		getIssueReferenceDetailsById(issueReferences, EIssueReferenceType.SPEC_ITEM, project, reThrowError)
	]);

	const referenceDetails = new Map([
		...Array.from(issueReferenceDetails.entries()),
		...Array.from(specReferenceDetails.entries())
	]);
	return {
		issueReferenceDetails,
		specReferenceDetails,
		referenceDetails
	};
}

async function getIssueReferenceDetailsById(
	issueReferences: IssueReference[],
	issueReferenceType: EIssueReferenceType,
	project: string,
	reThrowError = false
): Promise<Map<string, UserResolvedTeamscaleIssueWithType>> {
	const uniqueIssueReferenceIds = new Set(
		issueReferences
			.filter(issueReference => issueReference.type === issueReferenceType.name)
			.map(issueReference => issueReference.id.internalId)
	);
	let issueReferenceDetails: Array<UserResolvedSpecItem | UserResolvedTeamscaleIssue> = [];
	try {
		if (issueReferenceType === EIssueReferenceType.ISSUE) {
			issueReferenceDetails = (
				await QUERY.getIssuesDetails(project, {
					'issue-ids': Array.from(uniqueIssueReferenceIds)
				}).fetch()
			).filter(issue => issue != null);
		} else if (issueReferenceType === EIssueReferenceType.SPEC_ITEM) {
			issueReferenceDetails = (
				await QUERY.getSpecItemDetails(project, {
					'spec-item-ids': Array.from(uniqueIssueReferenceIds)
				}).fetch()
			).filter(specItem => specItem != null);
		}
	} catch (error) {
		if (reThrowError) {
			throw error;
		}
		ToastNotification.error(`References to ${issueReferenceType.readableName} could not be loaded.`);
	}

	return getIssueReferenceDetailsMap(issueReferenceDetails, issueReferenceType);
}

/** Get a map of the issue reference details. To each map value, the type of the reference is added. */
function getIssueReferenceDetailsMap(
	issueReferenceDetails: Array<UserResolvedSpecItem | UserResolvedTeamscaleIssue>,
	issueReferenceType: EIssueReferenceType
): Map<string, UserResolvedTeamscaleIssueWithType> {
	const issueReferenceDetailsById: Map<string, UserResolvedTeamscaleIssueWithType> = new Map<
		string,
		UserResolvedTeamscaleIssueWithType
	>();
	issueReferenceDetails.forEach(detail => {
		const detailWithType: UserResolvedTeamscaleIssueWithType = {
			...detail,
			type: issueReferenceType.name
		};
		issueReferenceDetailsById.set(detail.issue.id.internalId, detailWithType);
	});

	return issueReferenceDetailsById;
}
